import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Tag, ChevronLeft, MessageCircle, CheckCircle2, Phone, Info, AlertTriangle } from 'lucide-react';
import { Layout } from '../Layout';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { CallbackModal } from '../CallbackModal';
import { BlogFaqItem } from './BlogFaqItem';
import { RichText } from './RichText';
import { useTranslation } from '../../i18n';
import { blogUi, publishedLabel, updatedLabel } from '../../data/blog/blogCommon';
import { buildBlogSchema } from '../../utils/blogSchema';
import type { BlogArticle, BlogBlock, BlogLang } from '../../types/blog';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const themes = {
  teal: {
    heroBg: 'bg-teal-50',
    back: 'text-teal-600',
    tag: 'text-teal-700',
    avatar: 'bg-teal-200 text-teal-700',
    icon: 'text-teal-600',
    link: 'text-teal-700 font-medium underline underline-offset-2 hover:text-teal-900',
    stepBadge: 'bg-teal-600 text-white',
    value: 'text-teal-700',
    card: 'text-teal-600',
    ctaBg: 'bg-teal-900',
    ctaText: 'text-teal-100'
  },
  red: {
    heroBg: 'bg-red-50',
    back: 'text-red-600',
    tag: 'text-red-700',
    avatar: 'bg-red-200 text-red-700',
    icon: 'text-red-600',
    link: 'text-red-700 font-medium underline underline-offset-2 hover:text-red-900',
    stepBadge: 'bg-red-600 text-white',
    value: 'text-red-700',
    card: 'text-red-600',
    ctaBg: 'bg-red-900',
    ctaText: 'text-red-100'
  }
};
interface BlogArticleTemplateProps {
  article: BlogArticle;
}
export function BlogArticleTemplate({
  article
}: BlogArticleTemplateProps) {
  const {
    lang: rawLang
  } = useTranslation();
  const lang = (['en', 'ua', 'ru', 'ro'].includes(rawLang) ? rawLang : 'en') as BlogLang;
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const t = themes[article.theme];
  const renderBlock = (block: BlogBlock, idx: number) => {
    switch (block.type) {
      case 'p':
        return <p key={idx} className="mb-6" data-id="element-138" data-mp="2twibqkaj8tgh">
            <RichText text={block.text[lang]} lang={lang} linkClassName={t.link} data-id="element-139" data-mp="2tvgpdjo2gkjg" />
          </p>;
      case 'list':
        return <ul key={idx} className="space-y-3 mb-8" data-id="element-140" data-mp="33vhc7fl59ise">
            {block.items.map((item, i) => <li key={i} className="flex items-start" data-id="element-141" data-mp="37w18kgxw8idh" data-mpi={i}>
                <CheckCircle2 className={`w-5 h-5 mr-3 mt-1 flex-shrink-0 ${t.icon}`} aria-hidden="true" data-id="element-142" data-mp="37uq229d2cwio" />
                <span data-id="element-143" data-mp="3hmm764dkhmwe">
                  <strong className="text-gray-900" data-id="element-144" data-mp="3hluk5hy1cxhq">{item.title[lang]}</strong>{' '}
                  <RichText text={item.text[lang]} lang={lang} linkClassName={t.link} data-id="element-145" data-mp="3mmiby5u39id0" />
                </span>
              </li>)}
          </ul>;
      case 'steps':
        return <ol key={idx} className="space-y-5 mb-8" data-id="element-146" data-mp="1axu7ptxaiezh">
            {block.items.map((item, i) => <li key={i} className="flex items-start" data-id="element-147" data-mp="15y7i0l09z69s" data-mpi={i}>
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold mr-4 mt-0.5 flex-shrink-0 ${t.stepBadge}`} aria-hidden="true" data-id="element-148" data-mp="1613jgjbc0d2z">
                  {i + 1}
                </span>
                <span data-id="element-149" data-mp="1tp3i05evx7s4">
                  <strong className="block text-gray-900 mb-1" data-id="element-150" data-mp="1tqobrstdkmox">
                    {item.title[lang]}
                  </strong>
                  <RichText text={item.text[lang]} lang={lang} linkClassName={t.link} data-id="element-151" data-mp="1jz1qt4r8j98z" />
                </span>
              </li>)}
          </ol>;
      case 'table':
        return <div key={idx} className="bg-gray-50 rounded-xl p-6 mb-8 border border-gray-100" data-id="element-152" data-mp="2clmmuiino9iv">
            <div className="grid grid-cols-1 gap-4" data-id="element-153" data-mp="2252988vv9fya">
              {block.rows.map((row, i) => <div key={i} className="flex justify-between items-start gap-4 border-b border-gray-200 pb-3" data-id="element-154" data-mp="1n7s1namcgu7u" data-mpi={i}>
                  <span className="font-medium text-gray-900" data-id="element-155" data-mp="1s866ykwk9q26">
                    {row.label[lang]}
                  </span>
                  <span className={`font-bold text-right ${t.value}`} data-id="element-156" data-mp="2kvjwvz1udlcs">
                    {row.value[lang]}
                  </span>
                </div>)}
              {block.total && <div className="flex justify-between items-start gap-4 pt-2" data-id="element-157" data-mp="2b4p7iltau5n4">
                  <span className="font-bold text-gray-900 text-lg" data-id="element-158" data-mp="2yus0r58y9hnb">
                    {block.total.label[lang]}
                  </span>
                  <span className={`font-bold text-lg text-right ${t.value}`} data-id="element-159" data-mp="33mjpda8jpdat">
                    {block.total.value[lang]}
                  </span>
                </div>}
            </div>
            {block.note && <p className="mt-4 text-sm text-gray-500" data-id="element-160" data-mp="n5c2vo246qm5">
                <RichText text={block.note[lang]} lang={lang} linkClassName={t.link} data-id="element-161" data-mp="11vavzcetbbur" />
              </p>}
          </div>;
      case 'callout':
        {
          const isWarning = block.tone === 'warning';
          const Icon = isWarning ? AlertTriangle : Info;
          return <div key={idx} className={`flex items-start gap-3 rounded-xl p-5 mb-8 border ${isWarning ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-gray-50 border-gray-100 text-gray-700'}`} data-id="element-162" data-mp="i8d8r5igajny">
            <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" aria-hidden="true" data-id="element-163" data-mp="1tmqrjvyck8x4" />
            <p className="text-base leading-relaxed m-0" data-id="element-164" data-mp="1pm6usthor7bi">
              <RichText text={block.text[lang]} lang={lang} linkClassName={t.link} data-id="element-165" data-mp="1plnetiblit04" />
            </p>
          </div>;
        }
    }
  };
  return <Layout title={article.seoTitle[lang]} description={article.seoDescription[lang]} schema={buildBlogSchema(article, lang)} data-id="element-166" data-mp="r9ks71qfl9we">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-167" data-mp="w9yoxnml0xf4" />

      <article className="bg-white" data-id="element-168" data-mp="108oai3hrxl9u">
        {/* Header */}
        <div className={`${t.heroBg} py-12 lg:py-20`} data-id="element-169" data-mp="3lt5r5b6fghe">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-170" data-mp="8jmdw3w1ikkb">
            <Link to={`/${lang}/blog`} className={`inline-flex items-center font-medium mb-6 hover:underline ${t.back}`} data-id="element-171" data-mp="8juo1o0s3brt">
              <ChevronLeft className="w-4 h-4 mr-1" data-id="element-172" data-mp="hcy9ojlkuuxj" />
              {blogUi.back[lang]}
            </Link>

            <div className="flex flex-wrap gap-x-4 gap-y-2 mb-6 text-sm text-gray-600" data-id="element-173" data-mp="3gymthye3g0r5">
              <span className={`bg-white px-3 py-1 rounded-full font-medium flex items-center shadow-sm ${t.tag}`} data-id="element-174" data-mp="3kzya5eezpbn7">
                <Tag className="w-3 h-3 mr-2" data-id="element-175" data-mp="3dfe5yav0nfj6" />
                {article.category[lang]}
              </span>
              <span className="flex items-center" data-id="element-176" data-mp="38gkqwe02zggw">
                <Calendar className="w-4 h-4 mr-2" aria-hidden="true" data-id="element-177" data-mp="34fhe60dby0jw" />
                <time dateTime={article.datePublished} data-id="element-178" data-mp="2zi8ws200a0jp">
                  {publishedLabel[lang]}
                </time>
                <span className="mx-2" aria-hidden="true" data-id="element-179" data-mp="2ujo3kmj68h4r">
                  ·
                </span>
                <time dateTime={article.dateModified} data-id="element-180" data-mp="2pquyfxjsj6s5">{updatedLabel[lang]}</time>
              </span>
              <span className="flex items-center" data-id="element-181" data-mp="2ktmhg0adp8sh">
                <Clock className="w-4 h-4 mr-2" aria-hidden="true" data-id="element-182" data-mp="2gr7zrjig4f2s" />
                {article.readTime[lang]}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6" data-id="element-183" data-mp="2bqtpdutdgt1l">
              {article.h1[lang]}
            </h1>

            <div className="flex items-center" data-id="element-184" data-mp="3upzpc8sni3jn">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold mr-3 ${t.avatar}`} aria-hidden="true" data-id="element-185" data-mp="3uq9gw0tcgirg">
                DC
              </div>
              <div data-id="element-186" data-mp="3lr5y6xy3a5dh">
                <p className="font-medium text-gray-900" data-id="element-187" data-mp="3qo4nn36t5o5c">{blogUi.author[lang]}</p>
                <p className="text-sm text-gray-500" data-id="element-188" data-mp="3qq90iamopxhr">{blogUi.expert[lang]}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-189" data-mp="3gqqku6dt4xvi">
          <div className="prose prose-lg max-w-none text-gray-600" data-id="element-190" data-mp="32zd6r7yo4s7v">
            <p className="lead text-xl text-gray-800 font-medium mb-8" data-id="element-191" data-mp="3705gs3fd7157">
              <RichText text={article.intro[lang]} lang={lang} linkClassName={t.link} data-id="element-192" data-mp="36y2qrje7p80h" />
            </p>

            {article.sections.map((section, sIdx) => <section key={sIdx} data-id="element-193" data-mp="2xyfokvdnry6d" data-mpi={sIdx}>
                <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-194" data-mp="2y1jym9dt1o4y">
                  {section.heading[lang]}
                </h2>
                {section.blocks.map(renderBlock)}
              </section>)}

            <p className="mt-10 text-sm italic text-gray-500 border-t border-gray-100 pt-6" data-id="element-195" data-mp="2oblgcy73gclk">
              {blogUi.disclaimer[lang]}
            </p>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 pt-10 border-t border-gray-100" data-id="element-196" data-mp="2xcjlxkx6ks69">
            <h2 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-197" data-mp="2xaqfa5mki7y9">
              {blogUi.faqTitle[lang]}
            </h2>
            <div className="space-y-2" data-id="element-198" data-mp="2sbm3r8c67y4r">
              {article.faqs.map((faq, idx) => <BlogFaqItem key={idx} question={faq.q[lang]} answer={faq.a[lang]} isOpen={openFaqIndex === idx} onToggle={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)} lang={lang} accent={article.theme} data-id="element-199" data-mp="374fop9a7p6gt" data-mpi={idx} />)}
            </div>
          </div>

          {/* Read Also */}
          <div className="mt-16" data-id="element-200" data-mp="16qaqcagjlvh3">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-201" data-mp="11sscc5no4q28">
              {blogUi.readAlso[lang]}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-202" data-mp="wu7m8z24rmri">
              {article.readAlso.map(item => <Link key={item.to} to={`/${lang}${item.to}`} data-id="element-203" data-mp="1uhpu87nywkug">
                  <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-204" data-mp="1uf3nubctcbdt">
                    <span className={`font-medium ${t.card}`} data-id="element-205" data-mp="1pgjx7xn9ybf4">
                      {item.label[lang]}
                    </span>
                  </Card>
                </Link>)}
            </div>
          </div>
        </div>

        {/* CTA */}
        <section className={`py-16 ${t.ctaBg}`} data-id="element-206" data-mp="94wm9jsltwef">
          <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-207" data-mp="52rrghqf6tq7">
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-208" data-mp="518bks8oekoy">
              {blogUi.ctaTitle[lang]}
            </h2>
            <p className={`text-xl mb-8 max-w-2xl mx-auto ${t.ctaText}`} data-id="element-209" data-mp="2go22oc0g308y">
              {blogUi.ctaText[lang]}
            </p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap justify-center gap-4" data-id="element-210" data-mp="2pq1kv7n74adh">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-211" data-mp="2uh03wezkjmkf">
                <Button variant="primaryInverse" size="lg" className="w-full gap-2" data-id="element-212" data-mp="34ffsobb4ijqr">
                  <Calendar className="w-5 h-5" data-id="element-213" data-mp="34hah3zllstzl" />
                  {blogUi.bookOnline[lang]}
                </Button>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-214" data-mp="3dgnqytdn3kku">
                <Button variant="whatsappInverse" size="lg" className="w-full gap-2" data-id="element-215" data-mp="1j1uvqvgo4vtf">
                  <MessageCircle className="w-5 h-5" data-id="element-216" data-mp="2md0oz0xgot78" />
                  WhatsApp
                </Button>
              </a>
              <Link to={`/${lang}/prices`} className="w-full sm:w-auto" data-id="element-217" data-mp="2heh3t2qozlfx">
                <Button variant="outlineInverse" size="lg" className="w-full" data-id="element-218" data-mp="2vdp45yylczlp">
                  {blogUi.viewPrices[lang]}
                </Button>
              </Link>
              <Button variant="outlineInverse" size="lg" className="w-full sm:w-auto gap-2" onClick={() => setIsCallbackOpen(true)} data-id="element-219" data-mp="2qdarg3m1p1hc">
                <Phone className="w-5 h-5" data-id="element-220" data-mp="30ap8o5fsluap" />
                {blogUi.requestCall[lang]}
              </Button>
              {article.theme === 'red' && <a href="tel:+447345197820" className="w-full sm:w-auto" data-id="element-221" data-mp="3a3nz7suunuwy">
                  <Button variant="outlineInverse" size="lg" className="w-full gap-2" data-id="element-222" data-mp="3o1uo4jhtmkkh">
                    <Phone className="w-5 h-5" data-id="element-223" data-mp="3j3axw6w72mmb" />
                    {blogUi.callClinic[lang]}
                  </Button>
                </a>}
            </div>
          </div>
        </section>
      </article>
    </Layout>;
}